import React, { useMemo } from 'react';
import classnames from 'classnames';
import CardCommon from '@/pages/strategic/components/CardCommon';
import { TYPES, OVERVIEW_TIPS } from '@sg/pages/StudioManagement/LiveTitles/pages/LiveVisualizationAll/const';
import NumArrow from '@/pages/intelligence/pages/TopCharts/components/DetailsPage/NumArrow';
import { getNumConfig } from '@sg/utils/common';
import { mathFormat } from '@/pages/strategic/utils/common';
import IconTipsCom from '@sg/components/IconTipsCom';
import Loading from '@intelligence/components/Loading';
import { decimalFormat } from '@/pages/home/utils/common';
import { formatMainValue } from '@sg/pages/StudioManagement/LiveTitles/pages/LiveVisualizationAll/utils';
import type {
  IOverviewCardData,
  IOverviewMetric,
} from '@sg/pages/StudioManagement/LiveTitles/pages/LiveVisualizationAll/const';
interface IOverviewCardProps {
  data: IOverviewCardData;
  active?: boolean;
  type: string;
  onClick?: () => void;
  /** Appended to "Live-Ops Overview" title when exactly one studio is filtered. */
  singleSelectedStudioName?: string;
  loading?: boolean;
}

const CLS = 'dbs-lto-overview-card';

const MetricBlock: React.FC<{ metric?: IOverviewMetric; title?: string }> = ({ metric, title, unit = null, emptyText }) => {
  const newValue = decimalFormat(metric?.mom, 2, false);
  const numArrowData = getNumConfig(String(newValue), 0);
  let isNullData = false;
  if (!metric?.mom || !numArrowData || numArrowData.numSize === 'zero' || !metric?.mom || metric?.mom === '0' || metric?.mom === '0%') {
    isNullData = true;
  }

  return (
    <div className={`${CLS}__metric`}>
      <div className={`${CLS}__metric-title`}>{title}</div>
      <div className={`${CLS}__metric-value`}>
        {!metric?.value && emptyText ? (
          <span className={`${CLS}__empty-text`}>{emptyText}</span>
        ) : (
          formatMainValue(metric?.value, 'db_bmk00', unit)
        )}
      </div>
      <div className={`${CLS}__metric-dod`}>
        <div className="dbs-metrics-board-text-flex" key={title}>
          <div className="num-gray">{'MoM'}</div>
          {isNullData ? <span className="data-null">{'-'}</span> : <NumArrow dataContent={numArrowData} type={'num'} />}
        </div>
      </div>
      <ul className={`${CLS}__rank`}>
        {metric?.top3?.map((item, idx) => (
          <li key={item.game_title_abbr} className={`${CLS}__rank-item`}>
            <span className={classnames(`${CLS}__rank-index`, `${CLS}__rank-index--${idx + 1}`)}>{idx + 1}</span>
            <span className={`${CLS}__rank-name`}>{item.game_title_abbr || item.game_title}</span>
            <span className={`${CLS}__rank-value`}>{formatMainValue(item.value, 'db_bmk00', unit)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const OverviewCard: React.FC<IOverviewCardProps> = (props) => {
  const { data, active = false, onClick, type, singleSelectedStudioName, loading = false } = props;

  const isMc = useMemo(() => {
    return type === TYPES.mc;
  }, [type]);

  // 根据当前卡片类型取对应的提示文案，TipContext 内部会自动把 \n 渲染成 <br/>
  const tips = useMemo(
    () => (isMc ? OVERVIEW_TIPS.mc : OVERVIEW_TIPS.liveOps),
    [isMc],
  );

  return (
    <CardCommon
      cardKey={isMc ? 0 : 3}
      isActive={active ? 1 : 2}
      onClick={onClick}
      title={
        <div className="dbs-lto-overview-card__title-wrapper">
          <span>{isMc ? 'Miniclip Overview' : `Live-Ops Overview${singleSelectedStudioName ? `_${singleSelectedStudioName}` : ''}`}</span>
          <IconTipsCom
            tipString={tips}
            mWidth={520}
          />
        </div>
      }
      className="dbs-home-metric-card"
    >
      {loading ? (
        <Loading style={{ minHeight: '180px' }} />
      ) : (
      <div className={`${CLS}__body`}>
        <div className={`${CLS}__total`}>
          <div className={`${CLS}__total-label`}>{isMc ? 'Total Projects' : 'Total Live'}</div>
          <div className={`${CLS}__total-value`}>{data?.total_count ?? '-'}</div>
        </div>
        <div className={`${CLS}__metrics`}>
          <MetricBlock
            key={'gross_revenue'}
            metric={data?.gross_revenue}
            title={'Gross Revenue'}
            unit={'USD'}
            emptyText={isMc ? 'Data connecting' : null}
          />
          <MetricBlock
            key={'mau'}
            metric={data?.mau}
            title={'MAU'}
            emptyText={isMc ? 'Data connecting' : null}
          />
          <MetricBlock
            key={'avg_dau'}
            metric={data?.avg_dau}
            title={'AVG.DAU'}
            emptyText={isMc ? 'Data connecting' : null}
          />
        </div>
      </div>
      )}
    </CardCommon>
  );
};

export default OverviewCard;
